/**
 * HudHeader — top capsule with brand, telemetry, HUD expand, and thought ticker.
 * Extracted from Index.tsx for maintainability.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { NetworkState } from '@/types/game';
import type { BackendBuddyState } from '@/hooks/useBuddyBackend';
import type { MacroDisplayBundle } from '@/lib/types/macroDisplay';
import type { TransientReactionType } from '@/lib/types/events';

interface HudHeaderProps {
  network: NetworkState;
  bs: BackendBuddyState | null;
  hasBackend: boolean;
  blockJustFound: boolean;
  hudExpanded: boolean;
  setHudExpanded: (fn: (prev: boolean) => boolean) => void;
  worldDimming: boolean;
  activeSignature: string | null;
  displayVerdict: string;
  macro: MacroDisplayBundle;
  activeTransient: TransientReactionType | null;
  onShowGuide: () => void;
  onToggleDebug: () => void;
}

function HudDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 min-w-0">
      <span className="text-[6px] font-body font-medium uppercase tracking-wider block" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>{label}</span>
      <span className="text-[9px] font-body font-semibold tabular-nums block truncate" style={{ color: 'hsl(var(--foreground) / 0.7)' }}>{value}</span>
    </div>
  );
}

export default function HudHeader({
  network, bs, hasBackend, blockJustFound,
  hudExpanded, setHudExpanded, worldDimming,
  activeSignature, displayVerdict, macro,
  activeTransient, onShowGuide, onToggleDebug,
}: HudHeaderProps) {
  const [whyOpen, setWhyOpen] = useState(false);
  return (
    <motion.header data-share-exclude="true" className="w-full max-w-sm mx-auto px-3 pt-2.5 pb-0 relative z-10"
      initial={{ opacity: 0, y: -20 }} animate={{ opacity: worldDimming ? 0.3 : 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: worldDimming ? 0.8 : 0.3 }}>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'hsl(var(--foreground) / 0.07)',
          border: '1px solid hsl(var(--foreground) / 0.08)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 2px 12px hsl(var(--background) / 0.25), inset 0 1px 0 hsl(var(--foreground) / 0.04)',
        }}
      >
        {/* Row 1: Brand + expand toggle + Guidance */}
        <div className="flex items-center justify-between px-3.5 pt-2 pb-0.5">
          <button
            onClick={() => setHudExpanded(e => !e)}
            className="flex items-center gap-1.5 active:bg-foreground/[0.02] transition-colors"
          >
            <motion.p
              className="font-display text-[10px] font-semibold tracking-[0.1em] uppercase select-none whitespace-nowrap"
              style={{
                color: 'hsl(var(--bitcoin) / 0.75)',
                textShadow: '0 0 12px hsl(var(--bitcoin) / 0.12)',
              }}
              onDoubleClick={(e) => { e.stopPropagation(); onToggleDebug(); }}
            >
              Mempool <span style={{ color: 'hsl(var(--bitcoin) / 0.9)' }}>₿</span>uddy
            </motion.p>
            <span
              className="text-[7px] font-body font-medium tracking-[0.12em] uppercase"
              style={{
                color: 'hsl(var(--foreground) / 0.22)',
                borderLeft: '1px solid hsl(var(--foreground) / 0.1)',
                paddingLeft: 6,
                marginLeft: 2,
              }}
            >
              live companion
            </span>
            <motion.span
              animate={{ rotate: hudExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="text-[8px] ml-0.5"
              style={{ color: 'hsl(var(--foreground) / 0.18)' }}
            >
              ▾
            </motion.span>
          </button>
          <motion.button
            onClick={(e) => { e.stopPropagation(); onShowGuide(); }}
            className="shrink-0 flex items-center gap-1 text-[8px] font-body font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border"
            style={{
              color: 'hsl(var(--bitcoin))',
              backgroundColor: 'hsl(var(--bitcoin) / 0.08)',
              borderColor: 'hsl(var(--bitcoin) / 0.25)',
              textShadow: '0 0 8px hsl(var(--bitcoin) / 0.15)',
            }}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            📖 Guidance
          </motion.button>
        </div>

        {/* Row 2: Telemetry — Price / Fee / Block */}
        <button
          onClick={() => setHudExpanded(e => !e)}
          className="w-full grid grid-cols-3 items-center px-3.5 py-1 active:bg-foreground/[0.02] transition-colors"
        >
          {/* Left: BTC Price */}
          <div className="flex flex-col items-start gap-0">
            <div className="flex items-center gap-1">
              <motion.div
                className="w-[5px] h-[5px] rounded-full shrink-0"
                style={{
                  background: network.priceChange24h >= 0
                    ? 'hsl(var(--success) / 0.6)'
                    : 'hsl(var(--stress) / 0.6)',
                }}
                animate={{ scale: [1, 1.25, 1] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              />
              <span
                className="font-body font-bold text-[12px] tabular-nums tracking-tight"
                style={{ color: 'hsl(var(--foreground) / 0.9)' }}
              >
                ${network.btcPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </span>
            </div>
            <span
              className="text-[7px] font-body font-semibold tabular-nums ml-[9px]"
              style={{ color: network.priceChange24h >= 0 ? 'hsl(var(--success) / 0.65)' : 'hsl(var(--stress) / 0.65)' }}
            >
              {network.priceChange24h >= 0 ? '+' : ''}{network.priceChange24h.toFixed(1)}%
            </span>
          </div>

          {/* Center: Fee */}
          <div className="flex flex-col items-center gap-0">
            <span
              className="font-body font-bold text-[12px] tabular-nums"
              style={{ color: 'hsl(var(--foreground) / 0.75)' }}
            >
              {network.recommendedFees.medium}
            </span>
            <span
              className="text-[6px] font-body font-medium tracking-wider uppercase"
              style={{ color: 'hsl(var(--foreground) / 0.25)' }}
            >
              sat/vB
            </span>
          </div>

          {/* Right: Block */}
          <div className="flex flex-col items-end gap-0 relative">
            <AnimatePresence>
              {blockJustFound && (
                <motion.div
                  className="absolute -inset-2 rounded-xl pointer-events-none"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
                  style={{
                    background: 'radial-gradient(ellipse at 60% 50%, hsl(var(--bitcoin) / 0.12) 0%, transparent 70%)',
                  }}
                />
              )}
            </AnimatePresence>

            <div className="flex items-center gap-1">
              <div className="relative flex items-center justify-center" style={{ width: 8, height: 8 }}>
                {blockJustFound ? (
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ background: 'hsl(var(--bitcoin) / 0.8)' }}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: [0.5, 1.6, 1], opacity: [0, 1, 0.7] }}
                    transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                  />
                ) : (
                  <motion.div
                    className="absolute inset-0 rounded-full"
                    style={{ background: 'hsl(var(--foreground) / 0.15)' }}
                    animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
                <div
                  className="w-[4px] h-[4px] rounded-full relative z-10"
                  style={{
                    background: blockJustFound
                      ? 'hsl(var(--bitcoin) / 0.9)'
                      : 'hsl(var(--foreground) / 0.25)',
                    transition: 'background 0.4s ease',
                  }}
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.span
                  key={network.lastBlockHeight}
                  initial={blockJustFound ? { opacity: 0, y: -4 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="font-body font-bold text-[10px] tabular-nums"
                  style={{
                    color: blockJustFound
                      ? 'hsl(var(--bitcoin) / 0.9)'
                      : 'hsl(var(--foreground) / 0.7)',
                    transition: 'color 1.5s ease',
                  }}
                >
                  #{network.lastBlockHeight.toLocaleString()}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* Elapsed time */}
            {(() => {
              const elapsed = network.timeSinceLastBlock ?? 0;
              const mins = Math.floor(elapsed / 60);
              const secs = elapsed % 60;
              const overdue = elapsed > 600;
              const timerColor = overdue
                ? 'hsl(var(--stress) / 0.55)'
                : elapsed > 420
                  ? 'hsl(var(--bitcoin) / 0.4)'
                  : 'hsl(var(--foreground) / 0.28)';
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className="text-[7px] font-mono font-medium tabular-nums cursor-help"
                      style={{ color: timerColor }}
                    >
                      {mins}:{secs < 10 ? '0' : ''}{secs} ago
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="bottom" align="end" className="w-auto text-[10px] font-body px-3 py-2 rounded-lg space-y-1" style={{ background: 'hsl(var(--background) / 0.95)', border: '1px solid hsl(var(--foreground) / 0.1)' }}>
                    <p className="font-semibold" style={{ color: 'hsl(var(--foreground) / 0.8)' }}>⏱ Time since last block</p>
                    <div className="flex gap-3" style={{ color: 'hsl(var(--foreground) / 0.55)' }}>
                      <span>Avg: {Math.floor((network.avgBlockTimeSec ?? 600) / 60)}m {(network.avgBlockTimeSec ?? 600) % 60}s</span>
                      <span>·</span>
                      <span>Today: {network.blocksTodayCount ?? 0} blocks</span>
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })()}
          </div>
        </button>

        {/* Row 3: Why Buddy looks like this — replaces thought ticker */}
        <div
          className="mx-3"
          style={{ height: 1, background: 'linear-gradient(90deg, transparent, hsl(var(--foreground) / 0.06), transparent)' }}
        />
        <div className="flex items-center justify-center px-3 pb-1.5 pt-1">
          <motion.button
            onClick={() => setWhyOpen(o => !o)}
            className="flex items-center gap-1 text-[8px] font-body font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border"
            style={{
              color: 'hsl(var(--bitcoin))',
              backgroundColor: 'hsl(var(--bitcoin) / 0.08)',
              borderColor: 'hsl(var(--bitcoin) / 0.25)',
              textShadow: '0 0 8px hsl(var(--bitcoin) / 0.15)',
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            🔍 Why Buddy looks like this
            <motion.span
              animate={{ rotate: whyOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="text-[7px]"
            >
              ▾
            </motion.span>
          </motion.button>
        </div>
      </motion.div>

      {/* Why expanded content */}
      <AnimatePresence>
        {whyOpen && bs && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
            className="overflow-hidden mt-1.5"
          >
            <div
              className="rounded-2xl px-3.5 py-2.5"
              style={{
                background: 'hsl(var(--bitcoin) / 0.04)',
                border: '1px solid hsl(var(--bitcoin) / 0.08)',
                backdropFilter: 'blur(16px)',
              }}
            >
              <ul className="space-y-1">
                {(() => {
                  const reasons: string[] = [];
                  if (bs.weather === 'clear') reasons.push('Low fees and light congestion keep Buddy calm.');
                  else if (bs.weather === 'stormy') reasons.push('High congestion is driving stormy conditions.');
                  else if (bs.weather === 'fee-hurricane') reasons.push('Extreme fee pressure is creating hurricane conditions.');
                  else if (bs.weather === 'busy' || bs.weather === 'breezy') reasons.push('Moderate activity is keeping things breezy.');
                  if (bs.blockState === 'drought') reasons.push('Long block wait is driving current tension.');
                  else if (bs.blockState === 'delayed') reasons.push('Block is taking longer than usual.');
                  else if (bs.blockState === 'fresh' || bs.blockState === 'rapid') reasons.push('Recent block keeps block pressure low.');

                  // Short-term price impact (24h)
                  const p24 = network.priceChange24h;
                  if (p24 > 5) reasons.push(`Price surged ${p24.toFixed(1)}% in 24h — strong bullish energy right now.`);
                  else if (p24 > 2) reasons.push(`Price up ${p24.toFixed(1)}% today — adding optimism to Buddy's mood.`);
                  else if (p24 > 0) reasons.push(`Price slightly up (${p24.toFixed(1)}%) — keeping things stable.`);
                  else if (p24 > -2) reasons.push(`Price dipped slightly (${p24.toFixed(1)}%) — mild caution today.`);
                  else if (p24 > -5) reasons.push(`Price fell ${p24.toFixed(1)}% today — adding nervous energy.`);
                  else reasons.push(`Price dropped ${p24.toFixed(1)}% in 24h — significant stress on Buddy.`);

                  // Long-term price context (macro / 200WMA)
                  const macroState = macro.state;
                  if (macroState === 'DEEP_VALUE') reasons.push('Long-term: Price well below 200WMA — deep value territory, historically rare.');
                  else if (macroState === 'ACCUMULATION') reasons.push('Long-term: Price near 200WMA — accumulation zone, cautiously optimistic.');
                  else if (macroState === 'WARM') reasons.push('Long-term: Price above 200WMA — warming up, healthy bull conditions.');
                  else if (macroState === 'HOT') reasons.push('Long-term: Price extended above 200WMA — overheated, Buddy stays alert.');
                  else if (macroState === 'MIXED') reasons.push('Long-term: Mixed signals between valuation and trend — Buddy reads both sides.');
                  else reasons.push('Long-term: Macro conditions are neutral — no strong signal either way.');

                  if (bs.sentimentBucket === 'fear' || bs.sentimentBucket === 'extreme_fear') reasons.push('Fear sentiment is making the verdict more cautious.');
                  else if (bs.sentimentBucket === 'greed' || bs.sentimentBucket === 'extreme_greed') reasons.push('Greed sentiment adds confidence.');
                  if (activeTransient === 'blockFound') reasons.push('Block just found — Buddy is celebrating!');
                  else if (activeTransient === 'feeSpike') reasons.push('Fee spike reaction is active.');
                  if (bs.networkMood === 'SUSTAINED_CONGESTION') reasons.push('Sustained network congestion shapes long-term mood.');
                  if (bs.marketMood === 'SUSTAINED_BULLISH') reasons.push('Sustained bullish market adds long-term optimism.');
                  if (bs.marketMood === 'SUSTAINED_CAUTION') reasons.push('Sustained market caution weighs on outlook.');
                  if (reasons.length === 0) reasons.push('Buddy is observing conditions.');
                  return reasons.slice(0, 6).map((r, i) => (
                    <li key={i} className="text-[10px] font-body leading-snug flex items-start gap-1.5" style={{ color: 'hsl(var(--foreground) / 0.65)' }}>
                      <span style={{ color: 'hsl(var(--bitcoin) / 0.4)' }} className="mt-px">•</span>
                      {r}
                    </li>
                  ));
                })()}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {hudExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            className="overflow-hidden mt-1.5"
          >
            <div
              className="rounded-2xl px-3.5 py-2.5 space-y-2"
              style={{
                background: 'hsl(var(--foreground) / 0.06)',
                border: '1px solid hsl(var(--foreground) / 0.07)',
                backdropFilter: 'blur(16px)',
                boxShadow: '0 1px 8px hsl(var(--background) / 0.2), inset 0 1px 0 hsl(var(--foreground) / 0.03)',
              }}
            >
              {/* Mempool */}
              <div className="space-y-1">
                <span className="text-[7px] font-body font-medium tracking-widest uppercase" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                  Mempool
                </span>
                <div className="flex gap-3">
                  <HudDetailRow label="Size" value={`${((network.mempoolVsize ?? 0) / 1_000_000).toFixed(1)} MvB`} />
                  <HudDetailRow label="TXs" value={(network.pendingTxCount ?? 0).toLocaleString()} />
                  <HudDetailRow label="Fees" value={`${network.recommendedFees.low}/${network.recommendedFees.medium}/${network.recommendedFees.high}`} />
                </div>
              </div>
              {/* Mining */}
              <div className="space-y-1">
                <span className="text-[7px] font-body font-medium tracking-widest uppercase" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                  Mining
                </span>
                <div className="flex gap-3">
                  <HudDetailRow
                    label="Hashrate"
                    value={`${(network.hashrateChange7d ?? 0) > 0 ? '↑' : (network.hashrateChange7d ?? 0) < 0 ? '↓' : ''}${((network.hashrateTHs ?? 0) / 1e6).toFixed(0)} EH/s`}
                  />
                  <HudDetailRow label="Adj." value={`${(network.difficultyChange ?? 0) >= 0 ? '+' : ''}${(network.difficultyChange ?? 0).toFixed(1)}%`} />
                  <HudDetailRow label="Epoch" value={`${(network.difficultyProgress ?? 0).toFixed(1)}%`} />
                </div>
                <div className="h-1.5 rounded-full overflow-hidden mt-1.5" style={{ background: 'hsl(var(--foreground) / 0.06)' }}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: 'hsl(var(--bitcoin) / 0.4)' }}
                    animate={{ width: `${network.difficultyProgress ?? 0}%` }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                  />
                </div>
              </div>
              {/* Halving */}
              <div className="flex items-center justify-between">
                <span className="text-[7px] font-body font-medium uppercase tracking-wider" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                  Halving
                </span>
                <span
                  className="text-[8px] font-body font-medium tabular-nums"
                  style={{
                    color: (network.blocksToHalving ?? 0) < 144
                      ? 'hsl(var(--bitcoin) / 0.9)'
                      : (network.blocksToHalving ?? 0) < 2016
                        ? 'hsl(var(--bitcoin) / 0.7)'
                        : 'hsl(var(--foreground) / 0.5)',
                  }}
                >
                  {(network.blocksToHalving ?? 0) < 144
                    ? '🎰 TOMORROW'
                    : `${(network.blocksToHalving ?? 0).toLocaleString()} blocks · ~${Math.round(network.daysToHalving ?? 0)}d`}
                </span>
              </div>
              {/* Lightning */}
              {(network.lightningCapacitySats ?? 0) > 0 && (
                <div className="space-y-1">
                  <span className="text-[7px] font-body font-medium tracking-widest uppercase" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                    ⚡ Lightning
                  </span>
                  <div className="flex gap-3">
                    <HudDetailRow label="Capacity" value={`${Math.round((network.lightningCapacitySats ?? 0) / 1e8).toLocaleString()} BTC`} />
                    <HudDetailRow label="Channels" value={(network.lightningChannels ?? 0).toLocaleString()} />
                    <HudDetailRow label="Nodes" value={(network.lightningNodes ?? 0).toLocaleString()} />
                  </div>
                  <div className="h-0.5 rounded-full overflow-hidden mt-1" style={{ background: 'hsl(var(--foreground) / 0.04)' }}>
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: 'hsl(var(--success) / 0.3)' }}
                      animate={{ width: `${Math.min(100, ((network.lightningCapacitySats ?? 0) / (5000 * 1e8)) * 100)}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              )}
              {/* Last block */}
              <div className="flex items-center justify-between">
                <span className="text-[7px] font-body font-medium uppercase tracking-wider" style={{ color: 'hsl(var(--foreground) / 0.25)' }}>
                  Last block
                </span>
                <span className="text-[8px] font-body font-medium tabular-nums" style={{ color: 'hsl(var(--foreground) / 0.5)' }}>
                  {Math.floor((network.timeSinceLastBlock ?? 0) / 60)}m {(network.timeSinceLastBlock ?? 0) % 60}s ago
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
